import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight, FileText, Loader2, Shield, Trash2, UserX } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { GlassCard } from "@/components/GlassCard";
import { useLanguage } from "@/i18n/LanguageContext";
import { deleteProfileApi } from "@/lib/api";

const LegalRow = ({ to, icon: Icon, label, testId }) => (
  <Link
    data-testid={testId}
    to={to}
    className="flex items-center gap-3 rounded-xl px-2.5 py-2.5 -mx-1 hover:bg-white/5 transition-colors"
  >
    <div className="h-8 w-8 shrink-0 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
      <Icon size={14} className="text-cyan-300/80" />
    </div>
    <span className="flex-1 text-sm text-white/85">{label}</span>
    <ChevronRight size={15} className="text-white/30" />
  </Link>
);

/**
 * Phase 9: unobtrusive legal links + REAL permanent deletion
 * (server-side across all collections), distinct from the local-only
 * "Start over" reset.
 */
export const LegalLinksCard = ({ profileId, onDeleted }) => {
  const { t } = useLanguage();
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteProfileApi(profileId);
      toast.success(t("deleteProfile.success"));
      onDeleted();
    } catch {
      toast.error(t("deleteProfile.error"));
      setDeleting(false);
    }
  };

  return (
    <GlassCard className="p-4" data-testid="profile-legal-card">
      <p className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wider">
        {t("profile.legalTitle")}
      </p>

      <div className="mt-2 space-y-0.5">
        <LegalRow
          to="/privacy"
          icon={Shield}
          label={t("profile.legalPrivacy")}
          testId="profile-legal-privacy-link"
        />
        <LegalRow
          to="/terms"
          icon={FileText}
          label={t("profile.legalTerms")}
          testId="profile-legal-terms-link"
        />
        <LegalRow
          to="/delete-data"
          icon={UserX}
          label={t("profile.legalDeleteInfo")}
          testId="profile-legal-delete-data-link"
        />
      </div>

      <div className="mt-3 border-t border-white/10 pt-3">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              data-testid="profile-delete-account-button"
              className="flex w-full items-center gap-3 rounded-xl px-2.5 py-2.5 -mx-1 text-left hover:bg-red-500/10 transition-colors"
            >
              <div className="h-8 w-8 shrink-0 rounded-lg bg-red-500/10 border border-red-500/25 flex items-center justify-center">
                <Trash2 size={14} className="text-red-400" />
              </div>
              <span className="flex-1 text-sm font-medium text-red-400">
                {t("deleteProfile.button")}
              </span>
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent className="rounded-2xl bg-[#0B1220] border-white/10 max-w-sm">
            <AlertDialogHeader>
              <AlertDialogTitle>{t("deleteProfile.title")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("deleteProfile.desc")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                data-testid="delete-account-cancel-button"
                className="rounded-xl bg-white/5 border-white/10 hover:bg-white/10 hover:text-white"
              >
                {t("deleteProfile.cancel")}
              </AlertDialogCancel>
              <AlertDialogAction
                data-testid="delete-account-confirm-button"
                disabled={deleting}
                onClick={(e) => {
                  e.preventDefault();
                  handleDelete();
                }}
                className="rounded-xl bg-red-500/90 text-white hover:bg-red-500 disabled:opacity-60"
              >
                {deleting ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  t("deleteProfile.confirm")
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </GlassCard>
  );
};
